import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from '@/lib/msalConfig';
import { exchangeMicrosoftIdToken } from '@/lib/microsoftTokenExchange';
import { Spinner } from '@/components/ui/Spinner';

export function MicrosoftCallback() {
  const navigate = useNavigate();
  const { instance, inProgress, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [status, setStatus] = useState('Completing authentication...');
  const hasHandled = useRef(false);

  useEffect(() => {
    const handleAuth = async () => {
      // Prevent double-handling
      if (hasHandled.current) {
        return;
      }

      // Wait for MSAL to finish any interaction
      if (inProgress !== InteractionStatus.None) {
        return;
      }

      // Give MSAL a moment to fully process the redirect
      // This is needed because isAuthenticated may not update immediately
      if (!isAuthenticated && accounts.length === 0) {
        // Check if URL has auth response (code or error)
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(window.location.hash.replace('#', '?'));

        if (urlParams.has('code') || hashParams.has('code') || urlParams.has('error') || hashParams.has('error')) {
          // MSAL should be processing this, wait a bit more
          return;
        }

        // No auth response and not authenticated - redirect to login
        hasHandled.current = true;
        navigate('/login', { replace: true });
        return;
      }

      // Check if we're authenticated
      if (isAuthenticated && accounts.length > 0) {
        hasHandled.current = true;
        const account = accounts[0];
        setStatus('Syncing account...');

        // Exchange the Azure ID token (minted with our nonce by the login
        // redirect) for a real Supabase session. acquireTokenSilent returns
        // the just-issued cached token, so no network round-trip to Entra.
        // Best-effort: until RLS enforcement lands, the app still works for
        // MSAL-only users, and AppLayout retries the bridge — so a failed
        // exchange must not block entry.
        try {
          const result = await instance.acquireTokenSilent({
            scopes: loginRequest.scopes,
            account,
          });

          const exchange = await exchangeMicrosoftIdToken(result.idToken);
          if (!exchange.ok) {
            console.warn('[MicrosoftCallback] Supabase exchange failed:', exchange.reason);
          }
        } catch (err) {
          console.error('[MicrosoftCallback] Supabase exchange error:', err);
        }

        // Return the user to wherever they launched sign-in (e.g. the AI Import
        // gate set this), falling back to the dashboard for a normal login.
        // localStorage, so it survives the full-page redirect through Microsoft.
        const returnTo = localStorage.getItem('flow_post_login_redirect');
        localStorage.removeItem('flow_post_login_redirect');
        navigate(returnTo || '/', { replace: true });
      }
    };

    // Small delay to ensure MSAL has processed the redirect
    const timer = setTimeout(handleAuth, 100);
    return () => clearTimeout(timer);
  }, [inProgress, isAuthenticated, accounts, instance, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas px-4">
      <div className="text-center">
        <Spinner size="lg" />
        <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">{status}</p>
        <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
          {inProgress !== InteractionStatus.None ? `Processing: ${inProgress}` : 'Ready'}
        </p>
      </div>
    </div>
  );
}
